import { FC, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import cn from 'classnames';

import Loader from '../../components/Loader/Loader';
import Button from '../../components/Button/Button';
import Checkbox from '../../components/Checkbox/Checkbox';
import ContentWatchComponent from '../Alerts/ContentWatch/ContentWatch';
import useUnsubscribeSettings, { UnsubscribeConfirmStatus } from '../../core/apiHooks/useUnsubscribeSettings';
import { iContentWatch, SubscriptionType } from '../../core/models/ContentWatch';

import '../Alerts/Alerts.styles.scss';
import './Unsubscribe.styles.scss';

const INVALID_LINK_MESSAGE = 'This link is invalid or has expired';

const confirmMessages: Partial<Record<UnsubscribeConfirmStatus, string>> = {
    saved: 'Notification changes saved successfully',
    notFound: `${INVALID_LINK_MESSAGE}. Use the link in your most recent Report Pro email, or sign in to manage your alerts.`,
    invalid: 'We couldn’t save these settings. Review your choices and confirm again.',
    rateLimited: 'Too many attempts. Wait a minute, then confirm again.',
    error: 'We couldn’t save your changes. Check your connection and confirm again.'
};

const hiddenSavedIndicators = {
    frequency: false,
    contentPreference: false,
    tags: false,
    checkbox: false
};

const noSuggestedTags = {
    isLoading: false,
    isError: false
};

const Unsubscribe: FC = () => {
    const { token = '' } = useParams<{ token: string }>();
    const { loadStatus, maskedEmail, tags, subscriptionPackages, data, setData, confirm, confirmStatus } =
        useUnsubscribeSettings(token);

    const handleContentWatchUpdate = useCallback(
        (_section: string, updateData: Partial<iContentWatch>) => setData(updateData),
        [setData]
    );

    const handleSelectMyTopicsOnly = useCallback(() => setData({ subscriptionType: SubscriptionType.Related }), [setData]);

    const confirmMessage = confirmMessages[confirmStatus];

    return (
        <div className="Unsubscribe">
            <div className="Alerts Unsubscribe__container">
                <div className="Alerts__title">Notification settings</div>

                {loadStatus === 'loading' && <Loader />}

                {loadStatus === 'notFound' && (
                    <div className="Unsubscribe__message is-error" role="alert">
                        <b>{INVALID_LINK_MESSAGE}</b>
                        Use the link in your most recent Report Pro email, or sign in to Report Pro to manage your alerts.
                    </div>
                )}

                {loadStatus === 'error' && (
                    <div className="Unsubscribe__message is-error" role="alert">
                        <b>We couldn’t load your notification settings</b>
                        Refresh the page to try again.
                    </div>
                )}

                {loadStatus === 'ready' && (
                    <>
                        <div className="Alerts__sub-title">
                            <b>Choose which emails you’d like to receive.</b>
                            Changes apply to <i>{maskedEmail}</i> once you confirm them.
                        </div>
                        <div className="Alerts__content">
                            <div className="Alerts__section">
                                <div className="Alerts__section-header">
                                    <div className="Alerts__section-title">The Daily Dish: Food And Beverage News</div>
                                </div>
                                <div className="Alerts__section-content">
                                    Daily roundup of the F&B headlines you need to know
                                    <div className="Alerts__section-checkbox-wrapper">
                                        <Checkbox
                                            className="Alerts__section-checkbox"
                                            isChecked={data.isDailyDishEnabled}
                                            onChange={() => setData({ isDailyDishEnabled: !data.isDailyDishEnabled })}
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="Alerts__section">
                                <div className="Alerts__section-header">
                                    <div className="Alerts__section-title">Datassential Debrief: Insights In A Snap</div>
                                </div>
                                <div className="Alerts__section-content">
                                    Daily insights and trends distilled from our latest data
                                    <div className="Alerts__section-checkbox-wrapper">
                                        <Checkbox
                                            className="Alerts__section-checkbox"
                                            isChecked={data.isNewsArticleEnabled}
                                            onChange={() => setData({ isNewsArticleEnabled: !data.isNewsArticleEnabled })}
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="Alerts__divider" />

                            <ContentWatchComponent
                                data={data}
                                setData={handleContentWatchUpdate}
                                onSelectMyTopicsOnly={handleSelectMyTopicsOnly}
                                suggestedTags={noSuggestedTags}
                                showSavedIndicators={hiddenSavedIndicators}
                                lookups={{ tags, subscriptionPackages }}
                            />

                            <div className="Unsubscribe__footer">
                                <Button
                                    options={['primary']}
                                    onClick={confirm}
                                    isDisabled={confirmStatus === 'saving'}
                                    className="Unsubscribe__confirm"
                                >
                                    Confirm the changes
                                </Button>
                                {confirmMessage && (
                                    <div
                                        className={cn('Unsubscribe__message', confirmStatus !== 'saved' && 'is-error')}
                                        role={confirmStatus === 'saved' ? 'status' : 'alert'}
                                    >
                                        {confirmMessage}
                                    </div>
                                )}
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default Unsubscribe;
